/**
 * career-decision-profile — Workshop 3, »Wonach ich entscheide« (route `/workshops/profiler`).
 *
 * The W4 redesign of the Profiler. The retired version spent five recognition steps to
 * produce four numbers, and it could only ever produce a FIXED 0.40/0.25/0.25/0.10 spread:
 * a best_worst pick establishes an ORDER, not a magnitude, so the person could never say
 * how much more one thing weighed. Here they set the weights directly.
 *
 * The instrument that makes this a workshop rather than a settings page is the WORKED
 * EXAMPLE: a sample role scored, live, by the domain's own `computeMatchScore` /
 * `penaltyFromHits` / `tierForScore` — the same functions the search pipeline files a real
 * Match with. Move a weight and the score moves; add a red line and the example visibly
 * drops a tier. Nothing here re-implements the arithmetic; the math was hoisted into
 * `@career/definition-v2` precisely so this surface could run the real thing.
 *
 * It deliberately does NOT read the derived Schwartz magnitudes from W1. Those are a
 * separate, model-assisted derivation; a ranking instrument that silently inherited them
 * would inherit their errors too, and the person would not be able to see why their results
 * moved.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import {
  computeMatchScore,
  penaltyFromHits,
  tierForScore,
  type AlignmentDimension,
  type MatchSignal,
  type MatchTier,
  type ProfilerRubric,
  type ProvenanceBundle,
} from "@career/definition-v2"
import { I18nToken, RouterToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { hubHero } from "../lib/hub"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"

/** The transport envelope of `GET/POST /api/workshops/decision-profile`. */
interface DecisionProfileView {
  weights: { dimension: AlignmentDimension; label: string; weight: number }[]
  antiValues: string[]
  authored: boolean
}

/**
 * The gate slice of `GET /api/workshops` — the SAME list the Workshops hub reads. W3 sits
 * behind W2 server-side (`startWorkshop` → `prereqMet`), but the decision-profile endpoint
 * itself carries no gate, so without this the surface renders a fully editable rubric whose
 * every save throws the prereq error: the hub says »gesperrt« while the surface says »los«.
 * Reading the gate from the list keeps ONE source of truth and needs no contract change.
 */
interface WorkshopGateView {
  type: string
  gate: "locked" | "available" | "in_progress" | "completed"
  blockedBy: { slug: string; name: string } | null
}

/**
 * The sample role the example scores. Its per-dimension strengths are FIXED and openly
 * uneven — a role that fits the work well, pays into the direction moderately, and is
 * socially unremarkable. An even example would move identically under every weighting and
 * would therefore demonstrate nothing.
 */
const EXAMPLE_SIGNALS: readonly MatchSignal[] = [
  { dimension: "fit", strength: 0.9, claimRefs: [] },
  { dimension: "motivational", strength: 0.7, claimRefs: [] },
  { dimension: "goal", strength: 0.5, claimRefs: [] },
  { dimension: "social", strength: 0.25, claimRefs: [] },
]

/** One step of the +/− control, as a share of the total. */
const WEIGHT_STEP = 0.05

/** Common red lines, offered as one-tap toggles (the retired `dealbreakers` step's content). */
const REDLINE_CHOICES = [
  "Kultur, die Abkürzungen über Ehrlichkeit stellt",
  "Enge Kontrolle ohne eigenen Spielraum",
  "Arbeit ohne erkennbaren Sinn",
  "Keine Rücksicht auf das Leben außerhalb",
] as const

const TIER_KEY: Record<MatchTier, CareerKey> = {
  strong: "dp.tier.strong",
  viable: "dp.tier.viable",
  weak: "dp.tier.weak",
  unsuited: "dp.tier.unsuited",
}

/** Provenance for the throwaway rubric the PREVIEW scores with. It is never persisted —
 *  the server builds the durable one — but `computeMatchScore` takes a real rubric, and
 *  handing it a real one is what keeps the preview honest. */
const PREVIEW_PROV: ProvenanceBundle = {
  entity: { entityUrn: "urn:career:context:preview:profiler-rubric", entityType: "WorkshopContext" },
  activity: {
    runId: "decision-profile-preview",
    activityType: "decision-profile-preview",
    startedAt: "1970-01-01T00:00:00.000Z",
    associatedWith: [],
  },
  evidence: [],
}

export class DecisionProfileVm implements ViewModel {
  readonly view: Signal<DecisionProfileView | null> = signal<DecisionProfileView | null>(null)
  readonly gate: Signal<WorkshopGateView | null> = signal<WorkshopGateView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly saving: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.renderDeps = [this.view, this.gate, this.loaded, this.saving, this.error]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    try {
      const [view, workshops] = await Promise.all([
        apiGet<DecisionProfileView>("/api/workshops/decision-profile"),
        apiGet<WorkshopGateView[]>("/api/workshops"),
      ])
      this.view.set(view)
      this.gate.set(workshops.find((w) => w.type === "profiler") ?? null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Whether W2 is done, i.e. this workshop's weights can actually be saved. A missing gate
   *  (list unreachable) is treated as OPEN — a failed side-fetch must not lock a live surface. */
  get unlocked(): boolean {
    return this.gate.value?.gate !== "locked"
  }

  /** The workshop that has to come first, named honestly (AX-10: gated ≠ broken). */
  get blockedBy(): string | null {
    return this.gate.value?.blockedBy?.name ?? null
  }

  get weights(): DecisionProfileView["weights"] {
    return this.view.value?.weights ?? []
  }

  get antiValues(): string[] {
    return this.view.value?.antiValues ?? []
  }

  /** The rubric the PREVIEW scores with — built from what is on screen right now. */
  get previewRubric(): ProfilerRubric {
    return {
      id: "urn:career:context:preview:profiler-rubric",
      owner: "preview",
      kind: "profiler_rubric",
      criteria: this.weights.map(({ dimension, label, weight }) => ({ dimension, label, weight, description: label })),
      antiValues: this.antiValues,
      locationConstraint: null,
      prov: PREVIEW_PROV,
      createdAt: "1970-01-01T00:00:00.000Z",
    }
  }

  private async save(weights: DecisionProfileView["weights"], antiValues: readonly string[]): Promise<void> {
    if (this.saving.value) return
    this.saving.set(true)
    this.error.set(null)
    try {
      const body = { weights: weights.map(({ dimension, weight }) => ({ dimension, weight })), antiValues }
      this.view.set(await apiPost<DecisionProfileView>("/api/workshops/decision-profile", body))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.saving.set(false)
    }
  }

  /**
   * Nudge one dimension's weight. The others are NOT rescaled here — the server normalizes
   * to a sum of 1 on save, so the person always sees shares that add up, and the client
   * never has to reproduce that arithmetic.
   */
  async nudge(dimension: AlignmentDimension, direction: 1 | -1): Promise<void> {
    const next = this.weights.map((w) =>
      w.dimension === dimension ? { ...w, weight: Math.max(0, Math.min(1, w.weight + direction * WEIGHT_STEP)) } : w,
    )
    if (next.every((w) => w.weight === 0)) return // a rubric of all zeroes says nothing
    await this.save(next, this.antiValues)
  }

  async toggleRedline(value: string): Promise<void> {
    const current = this.antiValues
    await this.save(this.weights, current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
  }

  async addRedline(raw: string): Promise<void> {
    const value = raw.trim()
    if (value.length === 0 || this.antiValues.includes(value)) return
    await this.save(this.weights, [...this.antiValues, value])
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerDecisionProfile extends KitElement<DecisionProfileVm> {
  protected createViewModel(ctx: KitContext): DecisionProfileVm {
    return new DecisionProfileVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  /** One dimension row: label, share, and the two nudges. Flat rows + hairlines, no boxes. */
  private weightRow(vm: DecisionProfileVm, w: DecisionProfileView["weights"][number]): TemplateResult {
    const percent = Math.round(w.weight * 100)
    return html`
      <li class="dp-weight">
        <span class="dp-weight-label" lang="de">${w.label}</span>
        <span class="dp-weight-bar" aria-hidden="true"><span class="dp-weight-fill" style=${`width:${percent}%`}></span></span>
        <span class="dp-weight-value" lang="de">${percent}%</span>
        <span class="dp-weight-controls">
          <button class="sp-choice" data-act="dp-less" data-dimension=${w.dimension} aria-label=${`${vm.t("dp.weights.less")}: ${w.label}`} lang="de">−</button>
          <button class="sp-choice" data-act="dp-more" data-dimension=${w.dimension} aria-label=${`${vm.t("dp.weights.more")}: ${w.label}`} lang="de">+</button>
        </span>
      </li>
    `
  }

  /**
   * The worked example. Scored by the DOMAIN's functions, not a local copy — so the number
   * the person reads here is the number the pipeline would compute for those weights.
   */
  private example(vm: DecisionProfileVm): TemplateResult {
    const clean = computeMatchScore([...EXAMPLE_SIGNALS], vm.previewRubric)
    const penalised = penaltyFromHits(clean, vm.antiValues.length > 0 ? ["Beispieltreffer"] : [])
    const fmt = (n: number): string => n.toFixed(2)
    return html`
      <section class="sp-section" aria-labelledby="dp-example-title">
        <h2 id="dp-example-title" class="surface-section-title" lang="de">${vm.t("dp.example.title")}</h2>
        <p class="sp-hint" lang="de">${vm.t("dp.example.lead")}</p>
        <p class="dp-example-score" lang="de">
          ${vm.t("dp.example.score").replace("{score}", fmt(clean)).replace("{tier}", vm.t(TIER_KEY[tierForScore(clean)]))}
        </p>
        ${vm.antiValues.length > 0
          ? html`<p class="dp-example-penalty" lang="de">
              ${vm.t("dp.example.penalty").replace("{score}", fmt(penalised)).replace("{tier}", vm.t(TIER_KEY[tierForScore(penalised)]))}
            </p>`
          : nothing}
      </section>
    `
  }

  /** Shown while W2 is unfinished: the server-side prereq would reject every save, so name the
   *  blocking workshop and route into it instead of offering weights that cannot be stored.
   *  Mirrors the Suchprofil's own W1 gate — one gate idiom across the workshop surfaces. */
  private gate(vm: DecisionProfileVm): TemplateResult {
    const blocker = vm.blockedBy
    return html`<section class="sp-section sp-derive" aria-labelledby="dp-gate-title">
      <h2 id="dp-gate-title" class="surface-section-title" lang="de">${vm.t("dp.gate.title")}</h2>
      <p class="sp-hint" lang="de">${vm.t("dp.gate.lead")}</p>
      ${blocker ? html`<p class="sp-hint" lang="de">${vm.t("workshop.lockedBy", { name: blocker })}</p>` : nothing}
      <div class="sp-derive-actions">
        <button class="btn-primary" data-act="nav" data-url="/workshops/search-profile" lang="de">${vm.t("dp.gate.cta")}</button>
      </div>
    </section>`
  }

  protected render(vm: DecisionProfileVm): TemplateResult {
    if (!vm.loaded.value) return html`<p class="surface-lead" lang="de">${vm.t("dp.loading")}</p>`

    const decl = spineDecl("career-decision-profile")
    const chosen = vm.antiValues

    if (!vm.unlocked) {
      return html`
        <div class="sp-doc">
          ${hubHero({ eyebrow: vm.t("dp.eyebrow"), title: vm.t("dp.title"), lead: vm.t("dp.lead") })}
          ${this.gate(vm)}
        </div>
      `
    }

    return html`
      <div class="sp-doc">
        ${hubHero({
          eyebrow: vm.t("dp.eyebrow"),
          title: vm.t("dp.title"),
          lead: vm.t("dp.lead"),
          meta: vm.view.value?.authored === true ? vm.t("dp.state.authored") : vm.t("dp.state.default"),
        })}
        ${vm.error.value ? html`<p class="sp-error" role="alert" lang="de">${vm.error.value}</p>` : nothing}

        <section class="sp-section" aria-labelledby="dp-weights-title">
          <h2 id="dp-weights-title" class="surface-section-title" lang="de">${vm.t("dp.weights.title")}</h2>
          <p class="sp-hint" lang="de">${vm.t("dp.weights.hint")}</p>
          <ol class="dp-weights" role="list">${vm.weights.map((w) => this.weightRow(vm, w))}</ol>
        </section>

        <section class="sp-section" aria-labelledby="dp-redlines-title">
          <h2 id="dp-redlines-title" class="surface-section-title" lang="de">${vm.t("dp.redlines.title")}</h2>
          <p class="sp-hint" lang="de">${vm.t("dp.redlines.hint")}</p>
          <div class="sp-choices">
            ${REDLINE_CHOICES.map((choice) => {
              const on = chosen.includes(choice)
              return html`<button
                class=${classMap({ "sp-choice": true, "is-on": on })}
                data-act="dp-toggle-redline"
                data-value=${choice}
                aria-pressed=${on ? "true" : "false"}
                lang="de"
              >${choice}</button>`
            })}
          </div>
          <div class="sp-chips">
            ${chosen
              .filter((v) => !REDLINE_CHOICES.some((c) => c === v))
              .map(
                (value) => html`<span class="sp-chip">
                  <span lang="de">${value}</span>
                  <button class="sp-chip-x" data-act="dp-toggle-redline" data-value=${value} aria-label=${`${vm.t("sp.remove")}: ${value}`} lang="de">×</button>
                </span>`,
              )}
          </div>
          <form class="sp-addform">
            <input class="sp-add" type="text" name="redline" placeholder=${vm.t("dp.redlines.placeholder")} lang="de" />
            <button class="sp-addbtn" type="submit" data-act="dp-add-redline" lang="de">${vm.t("sp.add")}</button>
          </form>
        </section>

        ${this.example(vm)}
        ${nextStep(vm.t("next.eyebrow"), decl ? toNextStep(decl, vm.t(decl.labelKey)) : undefined)}
      </div>
    `
  }

  protected override actions(vm: DecisionProfileVm): ActionMap {
    const dimensionOf = (event: Event): AlignmentDimension | null => {
      const raw = (event.target as HTMLElement).closest<HTMLElement>("[data-dimension]")?.dataset.dimension
      return raw === "fit" || raw === "social" || raw === "motivational" || raw === "goal" ? raw : null
    }
    return {
      "dp-more": (_payload, event) => {
        const dimension = dimensionOf(event)
        if (dimension) void vm.nudge(dimension, 1)
      },
      "dp-less": (_payload, event) => {
        const dimension = dimensionOf(event)
        if (dimension) void vm.nudge(dimension, -1)
      },
      "dp-toggle-redline": ({ value }) => {
        if (value !== undefined) void vm.toggleRedline(value)
      },
      "dp-add-redline": (_payload, event) => {
        event.preventDefault()
        const input = (event.target as HTMLElement).closest("form")?.querySelector("input")
        if (!input) return
        const raw = input.value
        input.value = ""
        void vm.addRedline(raw)
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
